export class SliderHelper {
    /**
     * Считает только оригинальные слайды внутри wrapper.
     * Loop-клоны исключаются по атрибуту `loopCloneAttribute`, чтобы счётчик не учитывал дубли.
     */
    static getOriginalSlidesCount(slider, options = {}) {
        const {
            wrapperSelector = '.swiper-wrapper',
            slideSelector = '.swiper-slide',
            loopCloneAttribute = 'data-slider-loop-clone',
        } = options;

        const wrapper = slider?.querySelector(wrapperSelector);

        if (!wrapper) {
            return 0;
        }

        return wrapper.querySelectorAll(`${slideSelector}:not([${loopCloneAttribute}="true"])`).length;
    }

    /**
     * Находит элементы управления внутри конкретного слайдера по переданным селекторам.
     * Если селектор не передан, метод вернёт `null` для этого контрола.
     */
    static getSliderControls(slider, selectors = {}) {
        const {
            prevButton,
            nextButton,
            timeline,
            counter,
        } = selectors;

        return {
            prevButton: prevButton ? slider.querySelector(prevButton) : null,
            nextButton: nextButton ? slider.querySelector(nextButton) : null,
            timeline: timeline ? slider.querySelector(timeline) : null,
            counter: counter ? slider.querySelector(counter) : null,
        };
    }

    /**
     * Возвращает человекочитаемый номер текущего слайда, начиная с 1.
     * В loop-режиме нормализует индекс через количество оригинальных слайдов.
     */
    static getRealSlideNumber(swiper, originalSlidesCount) {
        const realIndex = Number.isFinite(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex || 0;

        return (realIndex % originalSlidesCount) + 1;
    }

    /**
     * Обновляет текстовый счётчик слайдера в формате `текущий/всего`.
     * Разделитель можно переопределить через `separator`.
     */
    static updateSliderCounter(swiper, counter, originalSlidesCount, options = {}) {
        const {
            separator = '/',
        } = options;

        if (!counter || !originalSlidesCount) {
            return;
        }

        counter.textContent = `${SliderHelper.getRealSlideNumber(swiper, originalSlidesCount)}${separator}${originalSlidesCount}`;
    }

    /**
     * Записывает прогресс таймлайна в CSS-переменную.
     * Значение ограничивается диапазоном от 0 до 1 и дополнительно синхронизируется с `aria-valuenow`.
     */
    static setSliderTimelineProgress(timeline, progress, options = {}) {
        const {
            progressVariable = '--slider-progress',
        } = options;

        if (!timeline) {
            return;
        }

        const normalizedProgress = Math.min(Math.max(progress, 0), 1);

        timeline.style.setProperty(progressVariable, normalizedProgress.toFixed(4));
        timeline.setAttribute('aria-valuenow', Math.round(normalizedProgress * 100));
    }

    /**
     * Подготавливает таймлайн как progressbar и сбрасывает его прогресс в 0.
     * `ariaLabel` и имя CSS-переменной можно переопределить под конкретный слайдер.
     */
    static prepareSliderTimeline(timeline, options = {}) {
        const {
            ariaLabel = 'Slide autoplay progress',
            progressVariable = '--slider-progress',
        } = options;

        if (!timeline) {
            return;
        }

        timeline.setAttribute('role', 'progressbar');
        timeline.setAttribute('aria-label', ariaLabel);
        timeline.setAttribute('aria-valuemin', '0');
        timeline.setAttribute('aria-valuemax', '100');

        SliderHelper.setSliderTimelineProgress(timeline, 0, { progressVariable });
    }

    /**
     * Дублирует оригинальные слайды, чтобы loop-режим Swiper работал стабильнее.
     * Повторный запуск блокируется атрибутом `loopPreparedAttribute`, а клоны помечаются `loopCloneAttribute`.
     */
    static duplicateSlidesForStableLoop(slider, copiesCount = 2, options = {}) {
        const {
            wrapperSelector = '.swiper-wrapper',
            slideSelector = '.swiper-slide',
            loopPreparedAttribute = 'data-slider-loop-prepared',
            loopCloneAttribute = 'data-slider-loop-clone',
        } = options;

        const wrapper = slider?.querySelector(wrapperSelector);

        if (!wrapper || wrapper.getAttribute(loopPreparedAttribute) === 'true') {
            return wrapper?.querySelectorAll(slideSelector).length || 0;
        }

        const originalSlides = Array.from(wrapper.querySelectorAll(slideSelector));

        for (let copyIndex = 0; copyIndex < copiesCount; copyIndex += 1) {
            originalSlides.forEach((slide) => {
                const clone = slide.cloneNode(true);
                clone.setAttribute(loopCloneAttribute, 'true');
                clone.setAttribute('aria-hidden', 'true');
                wrapper.appendChild(clone);
            });
        }

        wrapper.setAttribute(loopPreparedAttribute, 'true');

        return wrapper.querySelectorAll(slideSelector).length;
    }
}
